
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import { updatePageSEO } from "@/lib/seo";

const BlogPostPage = () => {
  const [match, params] = useRoute("/blog/:slug");
  const slug = params?.slug;

  const { data: post, isLoading, error } = useQuery<BlogPost>({
    queryKey: ["/api/blog", slug],
    queryFn: async () => {
      const response = await fetch(`/api/blog/${slug}`);
      if (!response.ok) {
        throw new Error("Blog post not found");
      }
      return response.json();
    },
    enabled: !!slug,
  });

  useEffect(() => {
    if (post) {
      updatePageSEO({
        title: `${post.title} - Diane Corso's Blog | The Corso Papers`,
        description: post.excerpt || `Read ${post.title} by Diane Corso, horror author of Broken Things. Insights into horror writing and the creative process.`,
        keywords: `Diane Corso, Diane Corso Author, ${post.title}, horror writing, Broken Things, The Corso Papers`,
        ogImage: post.featuredImage || "https://www.dianecorsofiction.com/attached_assets/broken things book rev (1)_1756509124921.webp",
        canonical: `https://www.dianecorsofiction.com/blog/${post.slug}`
      });
    }
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blood-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gothic-white">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-4xl font-cinzel font-bold text-blood-red mb-4">Post Not Found</h1>
          <p className="text-gray-300 mb-6">The blog post you're looking for doesn't exist or has been moved.</p>
          <Button 
            asChild
            className="bg-blood-red hover:bg-deep-red text-white"
          >
            <a href="/blog">
              <ArrowLeft className="mr-2" size={16} />
              Back to Blog
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <article className="py-20 bg-gothic-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Blog Link */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              asChild
              className="text-blood-red hover:text-gothic-white hover:bg-gothic-charcoal"
            >
              <a href="/blog" aria-label="Return to blog posts list">
                <ArrowLeft className="mr-2" size={16} />
                Back to Blog
              </a>
            </Button>
          </div>

          {/* Featured Image */}
          {post.featuredImage && (
            <div className="mb-8">
              <img 
                src={post.featuredImage} 
                alt={`${post.title} featured image`}
                className="w-full h-64 md:h-96 object-cover rounded-lg shadow-2xl"
                loading="eager"
                decoding="async"
                width="1200"
                height="600"
              />
            </div>
          )}

          {/* Post Header */}
          <header className="mb-8">
            <div className="flex items-center text-sm text-gray-400 mb-4">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{new Date(post.publishedAt!).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-cinzel font-bold text-blood-red mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="w-24 h-1 bg-blood-red"></div>
          </header>

          {/* Post Content */}
          <div className="prose prose-lg prose-invert max-w-none">
            <div className="text-gray-300 leading-relaxed space-y-6">
              {post.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: paragraph }} />
              ))}
            </div>
          </div>

          {/* Post Footer */}
          <footer className="mt-12 pt-8 border-t border-gothic-gray">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-xl font-playfair font-bold text-blood-red mb-2">About the Author</h3>
                <p className="text-gray-300">
                  Diane Corso is a San Francisco supernatural horror/thriller writer with a masterful command of psychological horror.
                </p>
              </div>
              <Button 
                asChild
                className="bg-blood-red hover:bg-deep-red text-white"
              >
                <a href="/about" aria-label="Read Diane Corso's full biography">Read Full Bio</a>
              </Button>
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
